import React from "react"
import { Divider, Form, Input, Select, Slider, Switch } from "antd"
import type { FormInstance } from "antd"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { Button } from "components"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { WorkplaceName } from "./SetupDutyLocations"
import SettingModification from "../../components/SettingModification"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"
import { useAppNotificationStore } from "stores"
import {
  NOTIFICATION_CADENCE_OPTIONS,
  sufficeIcon,
} from "../../components/NamePermissionClockIn"
import { HardwareDevices } from "../../typings"

// ─── Constants ────────────────────────────────────────────────────────────────

const HARDWARE_DEVICE_OPTIONS = [
  { label: "Fingerprint Device", value: "fingerprint" },
  { label: "Facial recognition", value: "facial_recognition" },
  { label: "Palm recognition", value: "palm_recognition" },
  { label: "RFID Cards", value: "rfid_card" },
  { label: "Device not listed here", value: "unknown" },
]

const BUFFER_WINDOW_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
]

const DEFAULT_INITIAL_VALUES = {
  allowManualTimeEntry: false,
  requireApprovalForManualTimeEntry: false,
  useInPayrollCalculation: true,
  allowSwapping: false,
  requireApprovalForSwap: false,
  allowLateClockIn: false,
  allowedLateClockinInMinutes: undefined as string | undefined,
  allowOvertime: false,
  useOvertimeRecord: false,
  linkHardwareDevice: false,
  hardwareDeviceTypes: [] as (string | HardwareDevices)[],
  hardwareSetupContactEmail: "",
  allowGeoFencing: false,
  geofencingOffsetMeters: 0,
  addOvertimeToPayroll: false,
  smsNotification: false,
  notificationCadence: undefined as string | undefined,
  notifiyMeBefore: false,
}

type ConfigFormValues = typeof DEFAULT_INITIAL_VALUES

// ─── Props ────────────────────────────────────────────────────────────────────

interface CreateShiftPlanConfigurationProps {
  shiftPlanName?: string
  formRef?: React.RefObject<FormInstance>
  initialValues?: Partial<ConfigFormValues>
  mode?: "create" | "edit"
  onSave?: () => void
  saveLoading?: boolean
}

// ─── Sub-components ───────────────────────────────────────────────────────────

export const IndentedSection: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="border-l-2 border-[#E2E8F0] pl-3 ml-1">{children}</div>

interface ConfirmOnDisable {
  title: string
  message: string
}

const ConfirmToggle: React.FC<{
  checked?: boolean
  onChange?: (checked: boolean) => void
  confirm: ConfirmOnDisable
}> = ({ checked, onChange, confirm }) => {
  const [open, setOpen] = React.useState(false)

  return (
    <>
      <Switch
        className="bg-[#ededed]"
        checked={checked}
        onChange={(next) => (next ? onChange?.(true) : setOpen(true))}
      />
      <CustomModal
        centered
        open={open}
        title={confirm.title}
        onCancel={() => setOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <Button
              color="secondary"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="destructive"
              onClick={() => {
                onChange?.(false)
                setOpen(false)
              }}
            >
              Disable
            </Button>
          </div>
        }
      >
        <p className="text-[#64748B]">{confirm.message}</p>
      </CustomModal>
    </>
  )
}

interface SettingRowProps {
  label: string
  description: string
  name: string
  confirmOnDisable?: ConfirmOnDisable
}

export const SettingRow: React.FC<SettingRowProps> = ({
  label,
  description,
  name,
  confirmOnDisable,
}) => (
  <div>
    <div className="flex items-center gap-3">
      <p className="font-semibold text-sm text-[#0F172B]">{label}</p>
      <Form.Item
        name={name}
        className="mb-0"
        valuePropName="checked"
        noStyle
      >
        {confirmOnDisable ? (
          <ConfirmToggle confirm={confirmOnDisable} />
        ) : (
          <Switch className="bg-[#ededed]" />
        )}
      </Form.Item>
    </div>
    <p className="text-sm text-[#64748B]">{description}</p>
  </div>
)

export const SectionDivider = () => (
  <Divider
    className="bg-slate-100"
    style={{ margin: "16px 0" }}
  />
)

// ─── Component ────────────────────────────────────────────────────────────────

const CreateShiftPlanConfiguration: React.FC<
  CreateShiftPlanConfigurationProps
> = ({
  shiftPlanName = "January shift plan",
  formRef,
  initialValues: initialValuesProp,
  mode,
  onSave,
  saveLoading = false,
}) => {
  const { toast } = useAppNotificationStore()
  const [canConfigureSettings] = usePermissions({
    allowedPermissions: [
      TimeAndAttendancePermission.CanConfigureAttendanceSettings,
    ],
  })
  const [form] = Form.useForm<ConfigFormValues>()
  const mergedInitialValues = React.useMemo(
    () => ({ ...DEFAULT_INITIAL_VALUES, ...initialValuesProp }),
    [initialValuesProp]
  )

  // Expose the form instance to the parent via formRef
  React.useEffect(() => {
    if (formRef) {
      ;(formRef as React.MutableRefObject<FormInstance | null>).current = form
    }
  }, [form, formRef])

  return (
    <Form
      form={form}
      layout="vertical"
      className="flex flex-col gap-6"
      initialValues={mergedInitialValues}
    >
      <h3 className="font-bold text-[18px] text-[#0F172B]">
        Configuration for <WorkplaceName name={shiftPlanName} />
      </h3>

      <div className="flex flex-col gap-0">
        <h4 className="font-bold text-[15px] text-[#0F172B] mb-4">
          Permissions &amp; clock-in rules
        </h4>

        <SettingModification
          BUFFER_WINDOW_OPTIONS={BUFFER_WINDOW_OPTIONS}
          NOTIFICATION_CADENCE_OPTIONS={NOTIFICATION_CADENCE_OPTIONS}
          HARDWARE_DEVICE_OPTIONS={HARDWARE_DEVICE_OPTIONS}
          sufficeIcon={sufficeIcon}
          form={form}
        />

        {mode === "edit" && onSave && (
          <div className="flex justify-end pt-4">
            <Button
              color="primary"
              className="font-semibold h-10"
              onClick={() => {
                if (!canConfigureSettings) {
                  toast.unauthorized()
                  return
                }
                onSave()
              }}
              submitting={saveLoading}
            >
              Save changes
            </Button>
          </div>
        )}
      </div>
    </Form>
  )
}

export default CreateShiftPlanConfiguration
